import { useEffect, useState } from 'react';
import type { MemberCreated, MemberRef, MeResponse } from '@kassa/shared';
import { formatDateTime } from '../format';
import { api, ApiError } from '../api';
import { InviteForm } from './members/InviteForm';
import { MemberForm } from './members/MemberForm';
import { AiAccessPanel } from './members/AiAccessPanel';
import { DevicesPanel } from './members/DevicesPanel';
import { CompanyPanel } from './members/CompanyPanel';
import { RolesPanel } from './members/RolesPanel';
import { PayMethodsPanel } from './members/PayMethodsPanel';
import { BonusSettingsPanel } from './members/BonusSettingsPanel';
import { TerminalsPanel } from './members/TerminalsPanel';
import { useConfirm } from '../components/ConfirmDialog';
import { PackagePanel } from './members/PackagePanel';
import { QuickButtonsPanel } from './members/QuickButtonsPanel';
import { DataTable, NewButton, PageHeader, useUrlFilters, type Column } from '../components/ListPage';

// Tənzimləmə (KT-99): bir səhifə, tablar. Qalan tablar (anbar, məhsul, sənəd nömrələri, bildirişlər, inteqrasiya,
// ilkin qalıqlar) sonrakı KT-lərdə.
const TABS: { key: string; label: string; permission: string | null }[] = [
  { key: 'isciler', label: 'İşçilər', permission: null },
  { key: 'rollar', label: 'Rollar', permission: 'settings.users' },
  { key: 'sirket', label: 'Şirkət', permission: 'settings.general' },
  { key: 'odenis', label: 'Ödəniş növləri', permission: 'settings.general' },
  { key: 'duymeler', label: 'Tez düymələr', permission: 'settings.general' },
  { key: 'cihazlar', label: 'Cihazlar', permission: 'settings.devices' },
  { key: 'terminallar', label: 'Terminallar', permission: 'settings.devices' },
  { key: 'ai', label: 'AI', permission: 'settings.users' },
  { key: 'paket', label: 'Paket', permission: null },
];
const KEYS = ['tab'] as const;

interface Props {
  me: MeResponse;
}

// İstifadəçilər və PİN (KT-38 PİN, KT-41 işçi): PİN heç vaxt ekranda göstərilmir.
// Müvəqqəti parol yalnız yaradılan/sıfırlanan anda, bir dəfə göstərilir və ekrandan silinir.
export function Pins({ me }: Props) {
  const confirm = useConfirm();
  const [members, setMembers] = useState<MemberRef[]>([]);
  const [editing, setEditing] = useState<string | null>(null);
  const [pin, setPin] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [adding, setAdding] = useState<false | 'worker' | 'accountant'>(false);
  const [inviting, setInviting] = useState(false);
  const [issued, setIssued] = useState<{ name: string; login: string | null; temporaryPassword: string } | null>(null);
  // Göndərilmiş dəvət: link yalnız bu anda var (məktub çatmasa rəhbər işçiyə özü versin); siyahıda saxlanmır
  const [invited, setInvited] = useState<{ name: string; link: string; expiresAt: string } | null>(null);
  const [copied, setCopied] = useState(false);
  const [f, setF] = useUrlFilters(KEYS);

  const load = () =>
    api
      .members()
      .then(setMembers)
      .then(() => setLoadError(null))
      .catch((err: unknown) => setLoadError(err instanceof ApiError ? err.message : 'Siyahı yüklənmədi'))
      .finally(() => setLoaded(true));

  useEffect(() => {
    load();
    // Dəyişiklik yalnız bu ekrandan olur; ilk yükləmə kifayətdir
  }, []);

  async function act(action: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await action();
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Əməliyyat alınmadı');
    } finally {
      setBusy(false);
    }
  }

  const savePin = (id: string) => {
    if (!/^\d{4,12}$/.test(pin)) {
      setError('PİN 4–12 rəqəm olmalıdır');
      return;
    }
    const value = pin;
    setPin('');
    setEditing(null);
    act(() => api.memberPinSet(id, value));
  };

  const clearPin = async (m: MemberRef) => {
    if (!(await confirm(`«${m.name}» üçün PİN silinsin?`, { confirmLabel: 'Sil', danger: true }))) return;
    act(() => api.memberPinClear(m.id));
  };

  const resetPassword = async (m: MemberRef) => {
    if (!(await confirm(`«${m.name}» üçün yeni müvəqqəti parol yaradılsın? Köhnə girişlər bağlanacaq.`, { confirmLabel: 'Parol yarat', danger: true }))) return;
    setBusy(true);
    setError(null);
    api
      .memberPasswordReset(m.id)
      .then((r) => {
        setInvited(null);
        setIssued({ name: m.name, login: null, temporaryPassword: r.temporaryPassword });
      })
      .catch((err: unknown) => setError(err instanceof ApiError ? err.message : 'Parol sıfırlanmadı'))
      .finally(() => setBusy(false));
  };

  const sendInvite = async (m: MemberRef) => {
    if (m.invite && !(await confirm(`«${m.name}» üçün dəvət yenidən göndərilsin? Əvvəlki link işləməyəcək.`, { confirmLabel: 'Göndər' }))) return;
    setBusy(true);
    setError(null);
    setIssued(null);
    setCopied(false);
    api
      .memberInviteSend(m.id)
      .then((r) => setInvited({ name: m.name, link: r.link, expiresAt: r.expiresAt }))
      .then(load)
      .catch((err: unknown) => setError(err instanceof ApiError ? err.message : 'Dəvət göndərilmədi'))
      .finally(() => setBusy(false));
  };

  const copyLink = (link: string) => {
    if (!navigator.clipboard) {
      setError('Kopyalanmadı — linki əl ilə seçin');
      return;
    }
    navigator.clipboard
      .writeText(link)
      .then(() => setCopied(true))
      .catch(() => setError('Kopyalanmadı — linki əl ilə seçin'));
  };

  const setActive = async (m: MemberRef) => {
    const next = !m.active;
    if (!next && !(await confirm(`«${m.name}» deaktiv edilsin? Girişi bağlanacaq, məlumatlar silinmir.`, { confirmLabel: 'Deaktiv et', danger: true }))) return;
    act(() => api.memberSetActive(m.id, next));
  };

  const onCreated = (r: MemberCreated, name: string) => {
    setAdding(false);
    setCopied(false);
    if (r.invite) {
      setIssued(null);
      setInvited({ name, link: r.invite.link, expiresAt: r.invite.expiresAt });
    } else {
      setInvited(null);
      setIssued({ name, login: r.login, temporaryPassword: r.temporaryPassword });
    }
    load();
  };

  const memberColumns: Column<MemberRef>[] = [
    {
      key: 'name',
      label: 'Ad',
      render: (m) => (
        <span className="product-name">
          {m.name}
          {m.locked && <span className="tag">kilidli</span>}
        </span>
      ),
    },
    { key: 'st', label: 'Status', render: (m) => <span className={m.active ? 'badge badge-ok' : 'badge badge-off'}>{m.active ? 'Aktiv' : 'Deaktiv'}</span> },
    { key: 'login', label: 'Giriş', minor: true, render: (m) => loginStatus(m) },
    { key: 'pin', label: 'PİN', minor: true, render: (m) => (m.hasPin ? 'PİN var' : 'PİN yoxdur') },
    {
      key: 'act',
      label: 'Əməliyyat',
      render: (m) =>
        editing === m.id ? (
          <span className="toolbar">
            <input type="password" inputMode="numeric" aria-label="Yeni PİN" value={pin} onChange={(e) => setPin(e.target.value)} autoComplete="off" />
            <button type="button" className="btn-small" onClick={() => savePin(m.id)} disabled={busy}>
              Yadda saxla
            </button>
            <button type="button" className="link-btn" onClick={() => { setEditing(null); setPin(''); }}>
              Ləğv et
            </button>
          </span>
        ) : (
          <span className="toolbar">
            <button type="button" className="btn-small" onClick={() => setEditing(m.id)} disabled={busy || !m.active}>
              {m.hasPin ? 'PİN dəyiş' : 'PİN qoy'}
            </button>
            {m.hasPin && (
              <button type="button" className="link-btn" onClick={() => clearPin(m)} disabled={busy}>
                PİN sil
              </button>
            )}
            {m.locked && (
              <button type="button" className="link-btn" onClick={() => act(() => api.memberUnlock(m.id))} disabled={busy}>
                Kilidi aç
              </button>
            )}
            {m.canInvite && !m.lastLoginAt && !m.invite?.usedAt && (
              <button type="button" className="link-btn" onClick={() => sendInvite(m)} disabled={busy || !m.active}>
                {m.invite ? 'Dəvəti yenidən göndər' : 'Giriş dəvəti göndər'}
              </button>
            )}
            <button type="button" className="link-btn" onClick={() => resetPassword(m)} disabled={busy || !m.active}>
              Parolu sıfırla
            </button>
            <button type="button" className="link-btn" onClick={() => setActive(m)} disabled={busy || m.id === me.tenant?.membershipId}>
              {m.active ? 'Deaktiv et' : 'Aktiv et'}
            </button>
          </span>
        ),
    },
  ];

  const tabs = TABS.filter((t) => t.permission === null || me.permissions?.includes(t.permission));
  const tab = tabs.find((t) => t.key === f.tab)?.key ?? tabs[0]?.key ?? 'isciler';

  return (
    <main className="page page-list">
      <PageHeader
        title="Tənzimləmə"
        actions={tab === 'isciler' && !adding ? <NewButton label="İşçi əlavə et" onClick={() => { setAdding('worker'); setIssued(null); setInvited(null); }} /> : undefined}
        tabs={
          <div role="tablist" aria-label="Tənzimləmə bölməsi" className="head-tabs">
            {tabs.map((t) => (
              <button key={t.key} type="button" role="tab" aria-selected={t.key === tab} className="head-tab" onClick={() => setF({ tab: t.key === tabs[0].key ? '' : t.key })}>
                {t.label}
              </button>
            ))}
          </div>
        }
      />

      {tab === 'isciler' && (
        <>
          <p className="muted small">PİN menecer təsdiqi üçündür (endirim, nisyə limiti, qaytarma). Yalnız rəqəm, 4–12.</p>

          {issued && (
            <section className="card lock-panel" role="status" aria-label="Müvəqqəti parol">
              <p className="ok">«{issued.name}» üçün müvəqqəti parol</p>
              {issued.login && (
                <p>
                  Login: <span className="mono" style={{ userSelect: 'all' }}>{issued.login}</span>
                </p>
              )}
              <p className="mono" style={{ fontSize: '1.25rem', userSelect: 'all' }}>{issued.temporaryPassword}</p>
              <p className="muted small">Bu parol yalnız indi göstərilir. İşçiyə verin; ilk girişdə yeni parol qoyacaq.</p>
              <button type="button" className="link-btn" onClick={() => setIssued(null)}>
                Gizlət
              </button>
            </section>
          )}

          {invited && (
            <section className="card lock-panel" role="status" aria-label="Giriş dəvəti">
              <p className="ok">«{invited.name}» üçün giriş dəvəti e-poçta göndərildi</p>
              <p className="muted small">
                İşçi məktubdakı linklə özü parol qoyacaq. Link bir dəfə işləyir, {formatDateTime(invited.expiresAt)}-dək keçərlidir. Məktub
                çatmasa, linki işçiyə özünüz verin (yalnız indi göstərilir).
              </p>
              <p className="mono small" style={{ wordBreak: 'break-all', userSelect: 'all' }}>{invited.link}</p>
              <span className="toolbar">
                <button type="button" className="btn-small" onClick={() => copyLink(invited.link)}>
                  {copied ? 'Kopyalandı' : 'Linki kopyala'}
                </button>
                <button type="button" className="link-btn" onClick={() => setInvited(null)}>
                  Gizlət
                </button>
              </span>
            </section>
          )}

          {adding && (
            <div className="modal-overlay">
              <div className="modal-box card" role="dialog" aria-modal="true" aria-label={adding === 'accountant' ? 'Yeni mühasib' : 'Yeni işçi'}>
                <MemberForm
                  firmName={me.tenant?.tenantName ?? ''}
                  presetRoles={adding === 'accountant' ? ['accountant'] : undefined}
                  onCreated={onCreated}
                  onCancel={() => setAdding(false)}
                />
              </div>
            </div>
          )}
          {!inviting && !adding && (
            <button type="button" className="link-btn" onClick={() => setInviting(true)} disabled={busy}>
              Mühasib dəvət et
            </button>
          )}
          {inviting && (
            <InviteForm
              onNewAccount={() => {
                setInviting(false);
                setAdding('accountant');
              }}
            />
          )}

          {error && (
            <p className="bad" role="alert">
              {error}
            </p>
          )}
          <DataTable
            label="İşçilər"
            columns={memberColumns}
            rows={members}
            rowKey={(m) => m.id}
            loading={!loaded}
            error={loadError}
            empty={<p className="muted">Üzv yoxdur.</p>}
          />
          <p className="muted small">Aktiv istifadəçi: {me.accountName}</p>
        </>
      )}
      {tab === 'rollar' && <RolesPanel />}
      {tab === 'sirket' && <CompanyPanel />}
      {tab === 'odenis' && (
        <>
          <PayMethodsPanel />
          {me.modules?.includes('bonus') && <BonusSettingsPanel />}
        </>
      )}
      {tab === 'duymeler' && <QuickButtonsPanel />}
      {tab === 'cihazlar' && <DevicesPanel tenantId={me.tenant?.tenantId} />}
      {tab === 'terminallar' && <TerminalsPanel me={me} />}
      {tab === 'ai' && <AiAccessPanel />}
      {tab === 'paket' && <PackagePanel />}
    </main>
  );
}

/** Giriş vəziyyəti (KT-114): girib, dəvət gözləyir, məktub çatmadı, link vaxtı keçib. */
function loginStatus(m: MemberRef): string {
  if (m.lastLoginAt) return `Son giriş: ${formatDateTime(m.lastLoginAt)}`;
  const inv = m.invite;
  // Dəvət mümkün deyil: e-poçt yoxdur və ya hesab başqa şirkətdə də işləyir (son girişi göstərilmir) — «girməyib» demirik
  if (!inv) return m.canInvite ? 'Dəvət göndərilməyib' : '—';
  if (inv.mailStatus === 'dead') return 'Dəvət məktubu çatmadı';
  if (inv.usedAt) return 'Parol qoyulub, hələ girməyib';
  if (new Date(inv.expiresAt).getTime() < Date.now()) return 'Dəvətin vaxtı keçib';
  // Gündəlik poçt limiti dolanda məktub növbədə gözləyir — «göndərildi» demirik
  if (inv.mailStatus === 'queued' || inv.mailStatus === 'sending') return `Dəvət növbədədir (${formatDateTime(inv.sentAt)}), poçt gözləyir`;
  return `Dəvət göndərildi: ${formatDateTime(inv.sentAt)}`;
}
